import { useEffect } from "react";
import { initializeApp, getApp, getApps, FirebaseApp } from "firebase/app";
import { getAnalytics } from "firebase/analytics";
import { getMessaging, Messaging } from "firebase/messaging";

export interface IFirebaseReturn {
  /** Initialized Firebase App */
  firebaseApp: FirebaseApp;
  /** Web FCM messaging instance (undefined if unsupported) */
  messaging?: Messaging;
}

const useFirebase = (): IFirebaseReturn => {
  // 1) Build config from .env
  const firebaseConfig = {
    apiKey: process.env.REACT_APP_API_KEY!,
    authDomain: process.env.REACT_APP_AUTH_DOMAIN!,
    projectId: process.env.REACT_APP_PROJECT_ID!,
    storageBucket: process.env.REACT_APP_STORAGE_BUCKET!,
    messagingSenderId: process.env.REACT_APP_MESSAGING_SENDER_ID!,
    appId: process.env.REACT_APP_APP_ID!,
    measurementId: process.env.REACT_APP_MEASUREMENT_ID!
  };

  // 2) Init Firebase App once
  const firebaseApp: FirebaseApp = getApps().length > 0 ? getApp() : initializeApp(firebaseConfig);

  // 3) Analytics on mount
  useEffect(() => {
    getAnalytics(firebaseApp);
  }, [firebaseApp]);

  // 4) Detect support for standard Web Push (exclude iOS Safari in-browser)
  let supportsWebPush = false;
  try {
    supportsWebPush =
      typeof window !== "undefined" &&
      "serviceWorker" in navigator &&
      "PushManager" in window &&
      "Notification" in window;
  } catch {
    supportsWebPush = false;
  }

  useEffect(() => {
    if (supportsWebPush) {
      navigator.serviceWorker
        .register("/firebase-messaging-sw.js")
        .then((_reg) => {
          // console.log("Service Worker registered successfully: ", reg.scope);
        })
        .catch((_err) => {
          // console.log("Service Worker registration failed: ", err);
        });
    }
  }, [supportsWebPush]);

  const isIosSafari =
    typeof window !== "undefined" &&
    /iP(ad|hone|od)/.test(navigator.platform) && // iOS device
    /Safari/.test(navigator.userAgent) && // Safari engine
    !/CriOS|FxiOS/.test(navigator.userAgent); // not Chrome/Firefox on iOS

  // 5) Prepare web Messaging instance only if truly supported
  let messaging: Messaging | undefined;
  if (supportsWebPush && !isIosSafari) {
    messaging = getMessaging(firebaseApp);
  } else if (isIosSafari) {
    // console.warn("📵 iOS Safari in-browser does not support Web Push.");
  }

  return { firebaseApp, messaging };
};

export default useFirebase;
